import { useCallback, useState } from 'react';
import { FlowTable } from '../FlowTable';
import { useSignalTableModel } from './useSignalTableModel';
import type { SignalRowWithComputed } from './signalsTableConfig';
import { SignalsToolbar } from './SignalsToolbar';

/**
 * The Signals tab's global mount (plan 2026-09-24-002 U5) — the SAME
 * pattern as `ContactFlowTable.tsx`'s global mount (`useFlowViewState` ->
 * `useViewStateMachine` -> `useFlowViewStatePersist` -> `<FlowTable
 * viewMachine config .../>`, `:456-620,1347-1395`), simplified: there is
 * only one mount (no account-scoped drill-down variant), no saved-column
 * selection UI, and no bulk actions yet (bead .16).
 *
 * Since bead terra-design-v2-table-tweaks-at4.17 (S4-U1, pure move), this is
 * a THIN RENDERER over `useSignalTableModel` — all data/state fetching lives
 * in the hook. This component keeps only the two renderer-owned concerns:
 * the group-collapse block below (chrome — V2 uses `useLedgerChrome`'s
 * instead) and `SignalsToolbar`.
 */
export function SignalsFlowTable({ fillHeight }: { fillHeight?: boolean }) {
  const { machine, rows, now, groupLevels, tableProps } = useSignalTableModel({ fillHeight });

  // QA batch 1 Q4 / D-B — Expand all / Collapse all, driven through FlowTable's
  // `groupCollapseCommand` prop (Q3). Keyed by the SAME formula as FlowTable's
  // own `collapseResetKey` (`${activeViewId} ${groupLevels.join(',')}`).
  //
  // Batch-1 review F1 (`fix-plan-batch1.md` D-A): the command used to carry
  // its own `key` and was merely HIDDEN behind a stale-key check, so
  // returning to the same view/grouping REVIVED the original command — the
  // button read "Expand all" over a table FlowTable had already reset to
  // fully expanded (FlowTable clears its own base collapse state on this
  // exact key change, `FlowTable.tsx:739-743`), and the click needed to fix
  // the mismatch sent an already-seen `seq` that FlowTable's `prevCommandSeq`
  // (which survives the key change by design — a same-seq re-render must
  // stay a no-op) correctly ignored as a no-op.
  //
  // Fixed by CLEARING the command (mode -> null) on the same render FlowTable
  // clears its base, instead of hiding it: `collapse.mode` is reset
  // render-time when `collapseKey` changes (the identical "adjust state
  // when a prop changes" shape FlowTable's own reset uses at :739-743 — no
  // effect, no lifetime ref; StrictMode-safe because the adjustment is
  // idempotent, so the second render sees the already-updated state). `seq`
  // is NEVER reset — it lives on the same state object as `mode`, so a view/
  // grouping round trip can never restart it, which is exactly what keeps
  // it monotonic against FlowTable's surviving `prevCommandSeq`.
  const collapseKey = `${machine.activeViewId} ${groupLevels.join(',')}`;
  const [collapse, setCollapse] = useState<{ seq: number; mode: 'expand' | 'collapse' | null }>({ seq: 0, mode: null });
  const [prevCollapseKey, setPrevCollapseKey] = useState(collapseKey);
  if (collapseKey !== prevCollapseKey) {
    setPrevCollapseKey(collapseKey);
    setCollapse(c => ({ seq: c.seq, mode: null }));
  }
  const activeCommand = collapse.mode ? { mode: collapse.mode, seq: collapse.seq } : null;
  const allCollapsed = activeCommand ? activeCommand.mode === 'collapse' : false;
  const toggleCollapseAll = useCallback(
    () => setCollapse(c => ({ seq: c.seq + 1, mode: allCollapsed ? 'expand' : 'collapse' })),
    [allCollapsed],
  );

  return (
    <FlowTable<SignalRowWithComputed>
      {...tableProps}
      groupCollapseCommand={activeCommand}
      toolbarTrailing={
        <SignalsToolbar
          rows={rows}
          now={now}
          groupCollapse={groupLevels.length > 0 ? { allCollapsed, onToggle: toggleCollapseAll } : null}
        />
      }
    />
  );
}
